import { useEffect, useState, useContext } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { LoaderContext } from "../../context/LoaderContext";
import {
  BLOCK_STATUS_OPTIONS,
  DISCREPANCY_TYPES,
  SUBJECT_STATUS_OPTIONS,
  TYPES_OF_REPORTS,
  USER_ROLES,
  VISIT_STATUSES,
} from "../../utils/constants";
import CustomButton from "../UI/CustomButton";

const SCORE_REPORT_NAMES = {
  [TYPES_OF_REPORTS.AI_SCORE]: "AI Score Report",
  [TYPES_OF_REPORTS.APP_PHOTO_SCORE]: "App Photos Manual Score Report",
  [TYPES_OF_REPORTS.EXTERNAL_PHOTO_SCORE]: "External Photos Manual Score Report",
};

const ReportDropdown = (props) => {
  const trialDetails = props.trialDetails;
  const setTrialDetails = props.setTrialDetails;
  const blocksData = props.blocksData;
  const changeLogBlockNameData = props.changeLogBlockNameData;
  const visitData = props.visitData;
  const AllTrialData = props.AllTrialData;
  const allInvestigators = props.allInvestigators;
  const AllCroData = props.AllCroData;
  const AllPhotoData = props.AllPhotoData;
  const AllVideoData = props.AllVideoData;
  const siteIdData = props.siteIdData;
  const participantIdData = props.participantIdData;
  const getDownloadDataView = props.getDownloadDataView;
  const REPORT_TYPE = props.REPORT_TYPE;
  const downloadReport = props.downloadReport;
  const reportDownloadLink = props.reportDownloadLink;
  const isLoading = props.isLoading;
  const isRunning = props.isRunning;
  const pdfProgress = props.pdfProgress || { current: 0, total: 0 };
  const handlePoemPdfDownload = props.handlePoemPdfDownload;
  const poemPdfProgress = props.poemPdfProgress || { current: 0, total: 0 };

  const isEcrfFilledPdf = trialDetails?.reportType?.value === "ecrf_filled_pdf";
  const isPdfGenerating = isEcrfFilledPdf && pdfProgress.total > 0;
  const isPoemPdfGenerating = poemPdfProgress.total > 0;
  const isScoreReport = Boolean(SCORE_REPORT_NAMES[trialDetails?.selected_report]);

  const loaderContext = useContext(LoaderContext);

  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const role = user_data.role;

  const [reportMultiOption, setReportMultiOption] = useState(true);
  const [downloadEcrfReportText, setDownloadEcrfReportText] = useState(
    "Download ECRF Report as XLSX"
  );

  const VISIT_STATUS = [
    { value: VISIT_STATUSES.COMPLETED, label: VISIT_STATUSES.COMPLETED },
    { value: VISIT_STATUSES.INCOMPLETE, label: VISIT_STATUSES.INCOMPLETE },
    { value: VISIT_STATUSES.NOT_OPENED, label: VISIT_STATUSES.NOT_OPENED },
    { value: VISIT_STATUSES.SKIPPED, label: VISIT_STATUSES.SKIPPED },
  ];

  const handleApplyFilters = () => {
    getDownloadDataView();
  };

  const trialsDisabled = !trialDetails.trials || trialDetails.trials.length === 0;

  const onMultiChange = (field, newValue, reason) => {
    let updatedValue;
    switch (reason) {
      case "removeOption":
        updatedValue = newValue;
        break;
      case "clear":
        updatedValue = (trialDetails[field] || []).filter((v) => v.isFixed);
        break;
      case "selectOption":
        if (field === "trials") {
          // Single-select behavior for trials: keep only the latest picked option
          const latestChangedObject = newValue.find(
            (option) => !trialDetails[field]?.some((v) => v.value === option.value)
          );
          updatedValue = latestChangedObject ? [latestChangedObject] : [];
        } else {
          updatedValue = newValue;
        }
        break;
      default:
        return;
    }
    setTrialDetails((trial) => ({
      ...trial,
      [field]: updatedValue,
    }));
  };

  const onSingleChange = (field, newValue) => {
    setTrialDetails((trial) => ({
      ...trial,
      [field]: newValue,
    }));
  };

  useEffect(() => {
    setTrialDetails((prev) => ({
      ...prev,
      visit_status: VISIT_STATUS,
    }));
  }, []);

  useEffect(() => {
    setTrialDetails((prev) => ({
      ...prev,
      patient_status: SUBJECT_STATUS_OPTIONS,
    }));
  }, []);

  useEffect(() => {
    if (!trialDetails?.reportType && trialDetails?.selected_report === TYPES_OF_REPORTS.ECRF) {
      setTrialDetails((prev) => ({
        ...prev,
        reportType: { value: "key_column", label: "Ecrf Report Key as Column" },
      }));
    }
  }, [trialDetails?.selected_report === TYPES_OF_REPORTS.ECRF]);

  useEffect(() => {
    if (
      trialDetails?.selected_report === TYPES_OF_REPORTS.ECRF_DISCREPANCY &&
      !trialDetails?.discrepancy_type
    ) {
      setTrialDetails((prev) => ({
        ...prev,
        discrepancy_type: DISCREPANCY_TYPES,
      }));
    }
  }, [trialDetails?.selected_report === TYPES_OF_REPORTS.ECRF_DISCREPANCY]);

  useEffect(() => {
    if (trialDetails?.reportType?.value === "filled_block") {
      setReportMultiOption(false);
      setDownloadEcrfReportText("Download ECRF Report as PDF");
    } else {
      setReportMultiOption(true);
      setDownloadEcrfReportText("Download ECRF Report as XLSX");
    }
  }, [trialDetails.reportType]);

  const isOptEqual = (option, value) => option.value === value.value;

  const investigatorSelect = (
    <Autocomplete
      multiple
      value={trialDetails?.investigators || []}
      options={allInvestigators || []}
      getOptionLabel={(o) => o?.label || ""}
      isOptionEqualToValue={isOptEqual}
      onChange={(_e, val, reason) => onMultiChange("investigators", val, reason)}
      disabled={trialsDisabled}
      renderInput={(params) => <TextField {...params} label="Investigator" size="small" />}
    />
  );

  const croSelect = (
    <Autocomplete
      multiple
      value={trialDetails?.cro || []}
      options={AllCroData || []}
      getOptionLabel={(o) => o?.label || ""}
      isOptionEqualToValue={isOptEqual}
      onChange={(_e, val, reason) => onMultiChange("cro", val, reason)}
      disabled={trialsDisabled}
      renderInput={(params) => <TextField {...params} label="CRO" size="small" />}
    />
  );

  const trialSelect = (
    <Autocomplete
      multiple
      value={trialDetails?.trials || []}
      options={AllTrialData || []}
      getOptionLabel={(o) => o?.label || ""}
      isOptionEqualToValue={isOptEqual}
      onChange={(_e, val, reason) => onMultiChange("trials", val, reason)}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Trial *"
          size="small"
          inputProps={{ ...params.inputProps, "data-testid": "report-trial-input" }}
        />
      )}
    />
  );

  const reportDownloadTypeSelect = (
    <Autocomplete
      value={trialDetails?.reportType || null}
      options={REPORT_TYPE || []}
      getOptionLabel={(o) => o?.label || ""}
      isOptionEqualToValue={isOptEqual}
      onChange={(_e, val) => onSingleChange("reportType", val)}
      disabled={trialsDisabled}
      renderInput={(params) => <TextField {...params} label="Select Report Type" size="small" />}
    />
  );

  const visitStatusSelect = (
    <Autocomplete
      multiple
      value={trialDetails?.visit_status || []}
      options={VISIT_STATUS}
      getOptionLabel={(o) => o?.label || ""}
      isOptionEqualToValue={isOptEqual}
      onChange={(_e, val, reason) => onMultiChange("visit_status", val, reason)}
      disabled={trialsDisabled}
      renderInput={(params) => <TextField {...params} label="Visit Status" size="small" />}
    />
  );

  const patientStatusSelect = (
    <Autocomplete
      multiple
      value={trialDetails?.patient_status || []}
      options={SUBJECT_STATUS_OPTIONS}
      getOptionLabel={(o) => o?.label || ""}
      isOptionEqualToValue={isOptEqual}
      onChange={(_e, val, reason) => onMultiChange("patient_status", val, reason)}
      disabled={trialsDisabled}
      renderInput={(params) => <TextField {...params} label="Subject Status" size="small" />}
    />
  );

  const photoTypeSelect = (
    <Autocomplete
      multiple
      value={trialDetails?.photo_type || []}
      options={AllPhotoData || []}
      getOptionLabel={(o) => o?.label || ""}
      isOptionEqualToValue={isOptEqual}
      onChange={(_e, val, reason) => onMultiChange("photo_type", val, reason)}
      disabled={trialsDisabled}
      renderInput={(params) => <TextField {...params} label="Photo Type" size="small" />}
    />
  );

  const videoTypeSelect = (
    <Autocomplete
      multiple
      value={trialDetails?.video_type || []}
      options={AllVideoData || []}
      getOptionLabel={(o) => o?.label || ""}
      isOptionEqualToValue={isOptEqual}
      onChange={(_e, val, reason) => onMultiChange("video_type", val, reason)}
      disabled={trialsDisabled}
      renderInput={(params) => <TextField {...params} label="Video Type" size="small" />}
    />
  );

  const siteIdSelect = (
    <Autocomplete
      multiple
      value={trialDetails?.site_number || []}
      options={siteIdData || []}
      getOptionLabel={(o) => o?.label || ""}
      isOptionEqualToValue={isOptEqual}
      onChange={(_e, val, reason) => onMultiChange("site_number", val, reason)}
      disabled={trialsDisabled}
      renderInput={(params) => <TextField {...params} label="Site Id" size="small" />}
    />
  );

  const participantIdSelect = (
    <Autocomplete
      multiple={reportMultiOption}
      value={
        reportMultiOption
          ? trialDetails?.participant_number || []
          : trialDetails?.participant_number?.[0] || null
      }
      options={participantIdData || []}
      getOptionLabel={(o) => o?.label || ""}
      isOptionEqualToValue={isOptEqual}
      onChange={(_e, val, reason) => {
        if (reportMultiOption) {
          onMultiChange("participant_number", val, reason);
        } else {
          // single-select: wrap in array for consistency
          onSingleChange("participant_number", val ? [val] : []);
        }
      }}
      disabled={trialsDisabled}
      renderInput={(params) => <TextField {...params} label="Subject Id" size="small" />}
    />
  );

  const blockWiseDownloadCheck = (
    <FormControlLabel
      control={
        <Checkbox
          checked={trialDetails?.block_wise_download || false}
          onChange={(e, checked) =>
            setTrialDetails((prev) => ({
              ...prev,
              block_wise_download: checked,
            }))
          }
          disabled={trialsDisabled}
        />
      }
      label="Bulk Report of Individual eCRFs"
    />
  );

  const extendedVisitReportCheck = (
    <FormControlLabel
      control={
        <Checkbox
          checked={trialDetails?.extended_visit_report || false}
          onChange={(e, checked) =>
            setTrialDetails((prev) => ({
              ...prev,
              extended_visit_report: checked,
            }))
          }
          disabled={trialsDisabled}
        />
      }
      label="Extended Visit Report"
    />
  );

  const poemIndividualVisitsCheck = (
    <FormControlLabel
      control={
        <Checkbox
          checked={trialDetails?.poem_individual_visits || false}
          onChange={(e, checked) =>
            setTrialDetails((prev) => ({
              ...prev,
              poem_individual_visits: checked,
            }))
          }
          disabled={trialsDisabled}
        />
      }
      label="Download individual visit POEMs as pdf"
    />
  );

  const extendedEcrfReportCheck = (
    <FormControlLabel
      control={
        <Checkbox
          checked={trialDetails?.extended_ecrf_report || false}
          onChange={(e, checked) =>
            setTrialDetails((prev) => ({
              ...prev,
              extended_ecrf_report: checked,
            }))
          }
          disabled={trialsDisabled}
        />
      }
      label="Extended Ecrf Report"
    />
  );

  const blockNameSelect = (
    <Autocomplete
      multiple
      value={trialDetails?.blocks || []}
      options={blocksData || []}
      getOptionLabel={(o) => o?.label || ""}
      isOptionEqualToValue={isOptEqual}
      onChange={(_e, val, reason) => onMultiChange("blocks", val, reason)}
      disabled={trialsDisabled}
      renderInput={(params) => <TextField {...params} label="Block Name" size="small" />}
    />
  );

  const discrepancyTypeSelect = (
    <Autocomplete
      multiple
      value={trialDetails?.discrepancy_type || []}
      options={DISCREPANCY_TYPES}
      getOptionLabel={(o) => o?.label || ""}
      isOptionEqualToValue={isOptEqual}
      onChange={(_e, val, reason) => onMultiChange("discrepancy_type", val, reason)}
      disabled={trialsDisabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Type of Discrepancy *"
          size="small"
          inputProps={{ ...params.inputProps, "data-testid": "discrepancy-type-input" }}
        />
      )}
    />
  );

  const changeLogBlockNameSelect = (
    <Autocomplete
      multiple
      value={trialDetails?.change_log_block_names || []}
      options={changeLogBlockNameData || []}
      getOptionLabel={(o) => o?.label || ""}
      isOptionEqualToValue={isOptEqual}
      onChange={(_e, val, reason) => onMultiChange("change_log_block_names", val, reason)}
      disabled={trialsDisabled}
      renderInput={(params) => <TextField {...params} label="Block Name" size="small" />}
    />
  );

  const blockStatusSelect = (
    <Autocomplete
      multiple
      value={trialDetails?.block_status || []}
      options={BLOCK_STATUS_OPTIONS}
      getOptionLabel={(o) => o?.label || ""}
      isOptionEqualToValue={isOptEqual}
      onChange={(_e, val, reason) => onMultiChange("block_status", val, reason)}
      disabled={trialsDisabled}
      renderInput={(params) => <TextField {...params} label="Block Status" size="small" />}
    />
  );

  const visitSelect = (
    <Autocomplete
      multiple
      value={trialDetails?.visit || []}
      options={visitData || []}
      getOptionLabel={(o) => o?.label || ""}
      isOptionEqualToValue={isOptEqual}
      onChange={(_e, val, reason) => onMultiChange("visit", val, reason)}
      disabled={trialsDisabled}
      renderInput={(params) => <TextField {...params} label="Visit" size="small" />}
    />
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, width: "100%" }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Filters:
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        {trialSelect}
        {trialDetails.selected_report === TYPES_OF_REPORTS.ECRF && (
          <>
            {reportDownloadTypeSelect}
            {!isEcrfFilledPdf && blockNameSelect}
            {visitSelect}
            {siteIdSelect}
            {participantIdSelect}
            {patientStatusSelect}
            {!isEcrfFilledPdf && blockWiseDownloadCheck}
            {!isEcrfFilledPdf && role === USER_ROLES.SUPER_ADMIN && extendedEcrfReportCheck}
          </>
        )}

        {trialDetails.selected_report === TYPES_OF_REPORTS.POEM && (
          <>
            {investigatorSelect}
            {visitSelect}
            {croSelect}
            {visitStatusSelect}
            {participantIdSelect}
            {siteIdSelect}
            {patientStatusSelect}
            {trialDetails.selected_report === TYPES_OF_REPORTS.POEM && poemIndividualVisitsCheck}
          </>
        )}

        {(trialDetails.selected_report === TYPES_OF_REPORTS.PHOTO ||
          trialDetails.selected_report === TYPES_OF_REPORTS.EXTERNAL_PHOTOS ||
          trialDetails.selected_report === TYPES_OF_REPORTS.EXTERNAL_DOCUMENTS) && (
          <>
            {visitSelect}
            {photoTypeSelect}
            {siteIdSelect}
            {participantIdSelect}
            {patientStatusSelect}
          </>
        )}

        {trialDetails.selected_report === TYPES_OF_REPORTS.EXTERNAL_VIDEOS && (
          <>
            {visitSelect}
            {videoTypeSelect}
            {siteIdSelect}
            {participantIdSelect}
            {patientStatusSelect}
          </>
        )}

        {trialDetails.selected_report === TYPES_OF_REPORTS.VIDEO && (
          <>
            {visitSelect}
            {siteIdSelect}
            {participantIdSelect}
            {patientStatusSelect}
          </>
        )}

        {trialDetails.selected_report === TYPES_OF_REPORTS.VISIT && (
          <>
            {visitSelect}
            {visitStatusSelect}
            {siteIdSelect}
            {participantIdSelect}
            {patientStatusSelect}
            {extendedVisitReportCheck}
          </>
        )}

        {trialDetails.selected_report === TYPES_OF_REPORTS.ADVANCED_SURVEY && (
          <>
            {visitSelect}
            {visitStatusSelect}
            {siteIdSelect}
            {participantIdSelect}
            {patientStatusSelect}
          </>
        )}

        {trialDetails.selected_report === TYPES_OF_REPORTS.EDIARY && (
          <>
            {visitStatusSelect}
            {siteIdSelect}
            {participantIdSelect}
            {patientStatusSelect}
          </>
        )}

        {isScoreReport && (
          <>
            {visitSelect}
            {siteIdSelect}
            {participantIdSelect}
            {patientStatusSelect}
          </>
        )}

        {trialDetails.selected_report === TYPES_OF_REPORTS.ECONSENT && (
          <>
            {siteIdSelect}
            {participantIdSelect}
            {patientStatusSelect}
          </>
        )}

        {trialDetails.selected_report === TYPES_OF_REPORTS.ECRF_CHANGE_LOGS && (
          <>
            {siteIdSelect}
            {participantIdSelect}
            {changeLogBlockNameSelect}
            {blockStatusSelect}
            {patientStatusSelect}
          </>
        )}

        {trialDetails.selected_report === TYPES_OF_REPORTS.QUERY_CHANGE_LOGS && (
          <>
            {siteIdSelect}
            {participantIdSelect}
            {patientStatusSelect}
          </>
        )}

        {trialDetails.selected_report === TYPES_OF_REPORTS.ECRF_DISCREPANCY && (
          <>
            {siteIdSelect}
            {participantIdSelect}
            {visitSelect}
            {discrepancyTypeSelect}
            {patientStatusSelect}
          </>
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        <CustomButton
          size="small"
          isLoading={loaderContext.loaderCount > 0 || isLoading || isRunning}
          disabled={loaderContext.loaderCount > 0 || isLoading || isRunning}
          onClick={handleApplyFilters}
          data-testid="apply-filters-btn"
        >
          Apply Filters
        </CustomButton>

        {trialDetails.selected_report === TYPES_OF_REPORTS.VISIT && (
          <CustomButton
            size="small"
            sx={{ ml: 1 }}
            disabled={loaderContext.loaderCount > 0 || !reportDownloadLink}
            onClick={downloadReport}
            data-testid="download-report-btn"
          >
            Download Visit Report
          </CustomButton>
        )}

        {trialDetails.selected_report === TYPES_OF_REPORTS.ECRF && !isEcrfFilledPdf && (
          <CustomButton
            size="small"
            sx={{ ml: 1 }}
            onClick={downloadReport}
            disabled={loaderContext.loaderCount > 0 || !reportDownloadLink}
            data-testid="download-report-btn"
          >
            {downloadEcrfReportText}
          </CustomButton>
        )}

        {isPdfGenerating && (
          <Box sx={{ ml: 2, display: "inline-flex", alignItems: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Generating PDF {pdfProgress.current} of {pdfProgress.total}…
            </Typography>
          </Box>
        )}

        {trialDetails.selected_report === TYPES_OF_REPORTS.POEM && (
          <CustomButton
            size="small"
            sx={{ ml: 1 }}
            onClick={downloadReport}
            disabled={loaderContext.loaderCount > 0 || !reportDownloadLink}
            data-testid="download-report-btn"
          >
            Download POEMs Report as XLSX
          </CustomButton>
        )}

        {trialDetails.selected_report === TYPES_OF_REPORTS.POEM && (
          <CustomButton
            size="small"
            sx={{ ml: 1 }}
            onClick={handlePoemPdfDownload}
            disabled={loaderContext.loaderCount > 0 || trialsDisabled || isPoemPdfGenerating}
            data-testid="download-poem-pdf-zip-btn"
          >
            Download POEMs PDFs as Zip
          </CustomButton>
        )}

        {isPoemPdfGenerating && (
          <Box sx={{ ml: 2, display: "inline-flex", alignItems: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Generating PDF {poemPdfProgress.current} of {poemPdfProgress.total}…
            </Typography>
          </Box>
        )}

        {trialDetails.selected_report === TYPES_OF_REPORTS.ADVANCED_SURVEY && (
          <CustomButton
            size="small"
            sx={{ ml: 1 }}
            onClick={downloadReport}
            disabled={loaderContext.loaderCount > 0 || !reportDownloadLink}
            data-testid="download-report-btn"
          >
            Download ePRO Report as XLSX
          </CustomButton>
        )}

        {trialDetails.selected_report === TYPES_OF_REPORTS.EDIARY && (
          <CustomButton
            size="small"
            sx={{ ml: 1 }}
            onClick={downloadReport}
            disabled={loaderContext.loaderCount > 0 || !reportDownloadLink}
            data-testid="download-report-btn"
          >
            Download eDiary Report as XLSX
          </CustomButton>
        )}

        {isScoreReport && (
          <CustomButton
            size="small"
            sx={{ ml: 1 }}
            onClick={downloadReport}
            disabled={loaderContext.loaderCount > 0 || !reportDownloadLink}
            data-testid="download-report-btn"
          >
            Download {SCORE_REPORT_NAMES[trialDetails.selected_report]} as XLSX
          </CustomButton>
        )}

        {trialDetails.selected_report === TYPES_OF_REPORTS.ECRF_CHANGE_LOGS && (
          <CustomButton
            size="small"
            sx={{ ml: 1 }}
            onClick={downloadReport}
            disabled={loaderContext.loaderCount > 0 || !reportDownloadLink}
            data-testid="download-report-btn"
          >
            Download Ecrf Change Logs Report as ZIP
          </CustomButton>
        )}

        {trialDetails.selected_report === TYPES_OF_REPORTS.QUERY_CHANGE_LOGS && (
          <CustomButton
            size="small"
            sx={{ ml: 1 }}
            onClick={downloadReport}
            disabled={loaderContext.loaderCount > 0 || !reportDownloadLink}
            data-testid="download-report-btn"
          >
            Download Query Change Logs Report as ZIP
          </CustomButton>
        )}

        {trialDetails.selected_report === TYPES_OF_REPORTS.ECRF_DISCREPANCY && (
          <CustomButton
            size="small"
            sx={{ ml: 1 }}
            onClick={downloadReport}
            disabled={loaderContext.loaderCount > 0 || !reportDownloadLink}
            data-testid="download-report-btn"
          >
            Download eCRF Discrepancy Report
          </CustomButton>
        )}
      </Box>
    </Paper>
  );
};

export default ReportDropdown;
